import { createAxiosInstance } from '../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

// Create axios instance with default config and interceptors
const visitRequestAPI = createAxiosInstance({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling (in addition to auth interceptor)
visitRequestAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    console.error('Visit Request API Error:', errorMessage);
    return Promise.reject(errorMessage);
  }
);

export const visitRequestService = {
  // Create a new visit request
  createVisitRequest: async (visitRequestData) => {
    try {
      const response = await visitRequestAPI.post('/visit-requests', visitRequestData);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to create visit request');
    }
  },

  // Get all visit requests (admin)
  getAllVisitRequests: async (params = {}) => {
    try {
      const { page = 0, size = 10, status, search } = params;
      const queryParams = { page, size };
      
      if (status && status !== 'all') {
        queryParams.status = status.toUpperCase();
      }
      
      if (search) {
        queryParams.search = search;
      }

      const response = await visitRequestAPI.get('/visit-requests', {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch visit requests');
    }
  },

  // Get visit requests by seller
  getVisitRequestsBySeller: async (sellerId, params = {}) => {
    try {
      const { page = 0, size = 10, status, date } = params;
      const queryParams = { page, size };
      
      if (status && status !== 'all') {
        queryParams.status = status.toUpperCase();
      }
      
      if (date) {
        queryParams.date = date;
      }

      const response = await visitRequestAPI.get(`/visit-requests/seller/${sellerId}`, {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch seller visit requests');
    }
  },

  // Get visit requests by customer
  getVisitRequestsByCustomer: async (customerId, params = {}) => {
    try {
      const { page = 0, size = 10, status } = params;
      const queryParams = { page, size };
      
      if (status && status !== 'all') {
        queryParams.status = status.toUpperCase();
      }

      const response = await visitRequestAPI.get(`/visit-requests/customer/${customerId}`, {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch customer visit requests');
    }
  },

  // Get a specific visit request by ID
  getVisitRequestById: async (requestId) => {
    try {
      const response = await visitRequestAPI.get(`/visit-requests/${requestId}`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch visit request');
    }
  },

  // Update visit request status
  updateVisitRequestStatus: async (requestId, status, notes = null) => {
    try {
      const response = await visitRequestAPI.patch(`/visit-requests/${requestId}/status`, {
        status: status.toUpperCase(),
        notes
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to update visit request status');
    }
  },

  // Mark visit as completed
  markVisitCompleted: async (requestId, completionData) => {
    try {
      const { purchaseMade = false, purchaseAmount = null, sellerNotes = null } = completionData;
      const response = await visitRequestAPI.patch(`/visit-requests/${requestId}/complete`, {
        purchaseMade,
        purchaseAmount,
        sellerNotes
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to mark visit as completed');
    }
  },

  // Delete visit request
  deleteVisitRequest: async (requestId) => {
    try {
      const response = await visitRequestAPI.delete(`/visit-requests/${requestId}`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to delete visit request');
    }
  },

  // Get visit request statistics for seller
  getVisitRequestStats: async (sellerId) => {
    try {
      const response = await visitRequestAPI.get(`/visit-requests/seller/${sellerId}/stats`);
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch visit request statistics');
    }
  },

  // Get visit request analytics for seller
  getVisitRequestAnalytics: async (sellerId, days = 30) => {
    try {
      const response = await visitRequestAPI.get(`/visit-requests/seller/${sellerId}/analytics`, {
        params: { days }
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to fetch visit request analytics');
    }
  },

  // Search visit requests
  searchVisitRequests: async (searchTerm, params = {}) => {
    try {
      const { page = 0, size = 10, sellerId, customerId, status } = params;
      const queryParams = { page, size, search: searchTerm };
      
      if (sellerId) {
        queryParams.sellerId = sellerId;
      }
      
      if (customerId) {
        queryParams.customerId = customerId;
      }
      
      if (status && status !== 'all') {
        queryParams.status = status.toUpperCase();
      }

      const response = await visitRequestAPI.get('/visit-requests/search', {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw new Error(error || 'Failed to search visit requests');
    }
  }
};

export default visitRequestService;